import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminActivities, type AdminActivityRow } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'
import { useMobile } from '../hooks/useMobile'
import PageHeader from './PageHeader'

type Tier = 'BASIC' | 'PLUS' | 'PREMIUM'

const TIERS: Tier[] = ['BASIC', 'PLUS', 'PREMIUM']

const TIER_COLORS: Record<Tier, { bg: string; text: string; border: string }> = {
  BASIC:   { bg: '#EDF6EE', text: '#2D6A4F', border: '#8FC89A' },
  PLUS:    { bg: '#FFF8EE', text: '#7A4F2D', border: '#E8C87A' },
  PREMIUM: { bg: '#F3EEFF', text: '#5B3A9E', border: '#B49FD8' },
}

const CLASSIC_SLUGS     = new Set(['memory', 'wordmatch', 'wordsearch'])
const ATTENTION_SLUGS   = new Set(['attention'])
const CROSSWORDS_SLUGS  = new Set(['crosswords'])

const GAME_EMOJIS: Record<string, string> = {
  memory:       '🃏',
  wordmatch:    '📖',
  cutlery:      '🍴',
  wordsearch:   '🔍',
  sudoku:       '🔢',
  maths:        '🧮',
  numbers:      '🔢',
  words:        '📝',
  shoppinglist: '🛒',
}

interface AdminActivitiesPageProps {
  onBack: () => void
}

export default function AdminActivitiesPage({ onBack }: AdminActivitiesPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useMobile()
  const [activities, setActivities] = useState<AdminActivityRow[]>([])
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState<string | null>(null)  // slug being saved
  const [error,      setError]      = useState<string | null>(null)
  const [saved,      setSaved]      = useState<string | null>(null)  // slug just saved
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    adminActivities.list()
      .then(setActivities)
      .catch(() => setError('Error al cargar las actividades'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const handleTierChange = async (slug: string, tier: Tier) => {
    setSaving(slug)
    setSaved(null)
    setError(null)
    try {
      const updated = await adminActivities.patch(slug, { requiredTier: tier })
      setActivities((prev) => prev.map((a) => a.slug === slug ? { ...a, ...updated } : a))
      setSaved(slug)
      setTimeout(() => setSaved(null), 2000)
    } catch {
      setError(`Error al actualizar ${slug}`)
    } finally {
      setSaving(null)
    }
  }

  const handleTogglePublished = async (slug: string, current: boolean) => {
    setSaving(slug)
    setSaved(null)
    setError(null)
    try {
      const updated = await adminActivities.patch(slug, { isPublished: !current })
      setActivities((prev) => prev.map((a) => a.slug === slug ? { ...a, ...updated } : a))
      setSaved(slug)
      setTimeout(() => setSaved(null), 2000)
    } catch {
      setError(`Error al actualizar ${slug}`)
    } finally {
      setSaving(null)
    }
  }

  const gameLabel = (slug: string): string =>
    (t.dashboard.games as Record<string, { label: string }>)[slug]?.label ?? slug

  return (
    <div style={s.page}>
      <PageHeader onBack={onBack} backLabel="← Admin" title="Actividades" />

      <div style={s.content}>
        {error && (
          <div style={s.errorBanner}>{error}</div>
        )}

        {loading ? (
          <p style={s.emptyText}>Cargando…</p>
        ) : isMobile ? (
          <div style={s.mobileList}>
            {activities.map((a) => {
              const isSaving = saving === a.slug
              const justSaved = saved === a.slug
              const tierColor = TIER_COLORS[a.requiredTier]
              return (
                <div
                  key={a.slug}
                  style={{ ...s.mobileCard, cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/activities/${a.slug}`)}
                >
                  <div style={s.mobileCardHeader}>
                    <span style={s.gameEmoji}>{GAME_EMOJIS[a.slug] ?? '🎮'}</span>
                    <div style={s.mobileCardInfo}>
                      <span style={s.mobileCardLabel}>{gameLabel(a.slug)}</span>
                      <span style={s.mobileCardSlug}>{a.slug}</span>
                    </div>
                    <span style={{
                      ...s.tierBadge,
                      backgroundColor: tierColor.bg,
                      color: tierColor.text,
                      borderColor: tierColor.border,
                    }}>
                      {a.requiredTier}
                    </span>
                  </div>
                  <div style={s.mobileCardRow}>
                    <span style={s.mobileCardMeta}>
                      {a.isPublished ? '✓ Visible' : '✗ Oculto'}
                    </span>
                    {a.category && (
                      <span style={s.mobileCardMeta}>📁 {a.category}</span>
                    )}
                    <span style={s.mobileCardDate}>
                      {new Date(a.updatedAt).toLocaleDateString('es-ES', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div style={s.mobileCardActions}>
                    {(isSaving || justSaved) && (
                      <span style={isSaving ? s.savingText : s.savedText}>
                        {isSaving ? 'Guardando…' : '✓ Guardado'}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={s.table}>
            {/* Header row */}
            <div style={s.headerRow}>
              <span style={{ ...s.cell, ...s.cellActivity }}>Actividad</span>
              <span style={{ ...s.cell, ...s.cellTier }}>Tier requerido</span>
              <span style={{ ...s.cell, ...s.cellPublished }}>Visible</span>
              <span style={{ ...s.cell, ...s.cellClassic }}>Contenido</span>
              <span style={{ ...s.cell, ...s.cellUpdated }}>Actualizado</span>
            </div>

            {activities.map((a) => {
              const isSaving = saving === a.slug
              const justSaved = saved === a.slug
              return (
                <div
                  key={a.slug}
                  style={{
                    ...s.row,
                    ...(isSaving ? s.rowSaving : {}),
                    ...(hoveredRow === a.slug ? { backgroundColor: '#FAFAF8' } : {}),
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/admin/activities/${a.slug}`)}
                  onMouseEnter={() => setHoveredRow(a.slug)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {/* Activity name */}
                  <div style={{ ...s.cell, ...s.cellActivity }}>
                    <span style={s.gameEmoji}>{GAME_EMOJIS[a.slug] ?? '🎮'}</span>
                    <div style={s.gameInfo}>
                      <span style={s.gameLabel}>{gameLabel(a.slug)}</span>
                      <span style={s.gameSlug}>{a.slug}</span>
                    </div>
                  </div>

                  {/* Tier selector */}
                  <div style={{ ...s.cell, ...s.cellTier }}>
                    <div style={s.tierBtns}>
                      {TIERS.map((tier) => {
                        const active = a.requiredTier === tier
                        const colors = TIER_COLORS[tier]
                        return (
                          <button
                            key={tier}
                            onClick={(e) => { e.stopPropagation(); if (!active) handleTierChange(a.slug, tier) }}
                            disabled={isSaving}
                            style={{
                              ...s.tierBtn,
                              backgroundColor: active ? colors.bg   : '#fff',
                              color:           active ? colors.text : '#888',
                              borderColor:     active ? colors.border: '#e0e0e0',
                              fontWeight:      active ? 700 : 500,
                              cursor:          active || isSaving ? 'default' : 'pointer',
                            }}
                            aria-pressed={active}
                          >
                            {tier}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Published toggle */}
                  <div style={{ ...s.cell, ...s.cellPublished }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePublished(a.slug, a.isPublished) }}
                      disabled={isSaving}
                      style={{
                        ...s.toggleBtn,
                        backgroundColor: a.isPublished ? '#E8F5EE' : '#F5F5F5',
                        color:           a.isPublished ? '#2D6A4F' : '#888',
                        borderColor:     a.isPublished ? '#8FC89A' : '#ddd',
                      }}
                      aria-pressed={a.isPublished}
                    >
                      {a.isPublished ? '✓ Sí' : '✗ No'}
                    </button>
                  </div>

                  {/* Classic content button */}
                  <div style={{ ...s.cell, ...s.cellClassic }}>
                    {CLASSIC_SLUGS.has(a.slug) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate('/admin/classic') }}
                        style={s.classicBtn}
                      >
                        🃏 Tarjetas
                      </button>
                    )}
                    {ATTENTION_SLUGS.has(a.slug) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate('/admin/words') }}
                        style={s.classicBtn}
                      >
                        📝 Words
                      </button>
                    )}
                    {CROSSWORDS_SLUGS.has(a.slug) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate('/admin/words') }}
                        style={s.classicBtn}
                      >
                        📝 Palabras
                      </button>
                    )}
                  </div>

                  {/* Updated at + save indicator */}
                  <div style={{ ...s.cell, ...s.cellUpdated }}>
                    {isSaving ? (
                      <span style={s.savingText}>Guardando…</span>
                    ) : justSaved ? (
                      <span style={s.savedText}>✓ Guardado</span>
                    ) : (
                      <span style={s.updatedText}>
                        {new Date(a.updatedAt).toLocaleDateString('es-ES', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <p style={s.note}>
          Los cambios de tier se aplican inmediatamente. El tier del usuario se refresca en el siguiente login.
        </p>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight:       '100vh',
    display:         'flex',
    flexDirection:   'column',
    background:      '#FAF6EF',
  },
  content: {
    flex:       1,
    padding:    '32px 24px',
    maxWidth:   900,
    width:      '100%',
    margin:     '0 auto',
    boxSizing:  'border-box',
  },
  errorBanner: {
    backgroundColor: '#FEE2E2',
    color:           '#991B1B',
    border:          '1px solid #FCA5A5',
    borderRadius:    10,
    padding:         '12px 16px',
    marginBottom:    20,
    fontSize:        '0.9rem',
  },
  emptyText: {
    color:    '#888',
    fontSize: '1rem',
  },

  table: {
    background:   '#fff',
    borderRadius: 16,
    border:       '1.5px solid #EFE7D7',
    overflowX:    'auto',
    boxShadow:    '0 2px 12px rgba(27,67,50,0.06)',
  },
  headerRow: {
    display:         'flex',
    alignItems:      'center',
    padding:         '12px 20px',
    background:      '#F7F3EE',
    borderBottom:    '1.5px solid #EFE7D7',
  },
  row: {
    display:      'flex',
    alignItems:   'center',
    padding:      '16px 20px',
    borderBottom: '1px solid #F0EBE3',
    transition:   'background 0.15s',
  },
  rowSaving: {
    opacity: 0.65,
  },

  cell: {
    display:    'flex',
    alignItems: 'center',
    fontSize:   '0.88rem',
    fontWeight: 600,
    color:      '#6B5744',
  },
  cellActivity: {
    flex:  '0 0 220px',
    gap:   12,
  },
  cellTier: {
    flex: 1,
  },
  cellPublished: {
    flex:           '0 0 90px',
    justifyContent: 'center',
  },
  cellClassic: {
    flex:           '0 0 130px',
    justifyContent: 'center',
  },
  cellUpdated: {
    flex:           '0 0 140px',
    justifyContent: 'flex-end',
  },

  gameEmoji: {
    fontSize:   '1.6rem',
    lineHeight: 1,
    flexShrink: 0,
  },
  gameInfo: {
    display:       'flex',
    flexDirection: 'column',
    gap:           2,
  },
  gameLabel: {
    fontSize:   '1rem',
    fontWeight: 700,
    color:      '#1B4332',
  },
  gameSlug: {
    fontSize: '0.875rem',
    color:       '#9A8070',
    fontFamily:  'monospace',
    fontWeight:  400,
  },

  tierBtns: {
    display: 'flex',
    gap:     6,
  },
  tierBtn: {
    minHeight:    36,
    padding:      '0 14px',
    fontSize: '0.875rem',
    letterSpacing:'0.04em',
    border:       '1.5px solid',
    borderRadius: 20,
    transition:   'all 0.15s',
  },

  toggleBtn: {
    minHeight:    36,
    minWidth:     64,
    padding:      '0 12px',
    fontSize: '0.875rem',
    fontWeight:   700,
    border:       '1.5px solid',
    borderRadius: 20,
    cursor:       'pointer',
    transition:   'all 0.15s',
  },

  savingText: {
    fontSize: '0.875rem',
    color:    '#B8860B',
    fontStyle:'italic',
  },
  savedText: {
    fontSize: '0.875rem',
    color:      '#2D6A4F',
    fontWeight: 700,
  },
  updatedText: {
    fontSize: '0.875rem',
    color:    '#9A8070',
  },

  note: {
    marginTop: 20,
    fontSize: '0.875rem',
    color:     '#9A8070',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },

  classicBtn: {
    fontSize: '0.875rem',
    fontWeight:   700,
    color:        '#2D6A4F',
    background:   '#F0FAF4',
    border:       '1.5px solid #B7E4C7',
    borderRadius: 20,
    padding:      '6px 14px',
    cursor:       'pointer',
    minHeight:    36,
    whiteSpace:   'nowrap',
  },

  // Mobile card view
  mobileList: {
    display:    'flex',
    flexDirection: 'column',
    gap:       12,
  },
  mobileCard: {
    background:   '#fff',
    borderRadius: 14,
    border:       '1.5px solid #EFE7D7',
    boxShadow:    '0 2px 12px rgba(27,67,50,0.06)',
    padding:      '16px',
  },
  mobileCardHeader: {
    display:    'flex',
    alignItems: 'center',
    gap:        12,
    marginBottom: 12,
  },
  mobileCardInfo: {
    display:       'flex',
    flexDirection: 'column',
    gap:           2,
    flex:          1,
  },
  mobileCardLabel: {
    fontSize:   '1.05rem',
    fontWeight: 700,
    color:      '#1B4332',
  },
  mobileCardSlug: {
    fontSize: '0.875rem',
    color:      '#9A8070',
    fontFamily: 'monospace',
    fontWeight: 400,
  },
  tierBadge: {
    fontSize: '0.875rem',
    fontWeight:   700,
    letterSpacing: '0.04em',
    border:       '1.5px solid',
    borderRadius: 16,
    padding:      '4px 10px',
    flexShrink:   0,
  },
  mobileCardRow: {
    display:       'flex',
    alignItems:    'center',
    gap:           12,
    marginBottom:  12,
    flexWrap:      'wrap',
  },
  mobileCardMeta: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#6B5744',
  },
  mobileCardDate: {
    fontSize: '0.875rem',
    color:    '#9A8070',
    marginLeft: 'auto',
  },
  mobileCardActions: {
    display:       'flex',
    alignItems:    'center',
    gap:           8,
  },
}
